import type {
  UserInvitation,
  CreateInvitationInput,
  CompleteInvitationInput,
  VerifyInvitationResponse,
  PaginatedInvitations,
  UserRole,
} from '@lnr-v2/types';
import { apiClient } from './api.client';
import { HTTPError } from 'ky';

/**
 * Database invitation response (matches backend)
 */
interface DbInvitation {
  id: string;
  email: string;
  role: 'SUPER_ADMIN' | 'ADMIN' | 'EDITOR' | 'VIEWER';
  status: 'PENDING_VERIFICATION';
  invitedBy: string;
  invitedByName: string;
  invitationSentAt: string;
  invitationExpiry: string;
}

/**
 * Map database role to frontend role
 */
function mapDbRoleToFrontend(
  dbRole: 'SUPER_ADMIN' | 'ADMIN' | 'EDITOR' | 'VIEWER'
): UserRole {
  return dbRole.toLowerCase().replace('_', '_') as UserRole;
}

/**
 * Map frontend role to database role
 */
function mapFrontendRoleToDb(
  frontendRole: UserRole
): 'SUPER_ADMIN' | 'ADMIN' | 'EDITOR' | 'VIEWER' {
  return frontendRole.toUpperCase().replace('_', '_') as
    | 'SUPER_ADMIN'
    | 'ADMIN'
    | 'EDITOR'
    | 'VIEWER';
}

/**
 * Transform database invitation to frontend format
 */
function transformDbInvitationToFrontend(
  dbInvitation: DbInvitation
): UserInvitation {
  return {
    id: dbInvitation.id,
    email: dbInvitation.email,
    role: mapDbRoleToFrontend(dbInvitation.role),
    status: 'PENDING' as const,
    invitedBy: dbInvitation.invitedBy,
    invitedByName: dbInvitation.invitedByName,
    invitationSentAt: dbInvitation.invitationSentAt,
    invitationExpiry: dbInvitation.invitationExpiry,
  };
}

/**
 * Invitation Service
 * Handles all API calls related to user invitations
 */
export const invitationService = {
  /**
   * Create a new user invitation
   */
  async createInvitation(input: CreateInvitationInput): Promise<UserInvitation> {
    try {
      const response = await apiClient.post('invitations', {
        json: {
          email: input.email,
          role: mapFrontendRoleToDb(input.role),
        },
      });

      const data = await response.json<{
        invitation: DbInvitation;
        message: string;
      }>();

      return transformDbInvitationToFrontend(data.invitation);
    } catch (error) {
      console.error('Error creating invitation:', error);
      if (error instanceof HTTPError) {
        const errorData = await error.response.json<{ error: string }>();
        throw new Error(errorData.error || 'Failed to send invitation');
      }
      throw error;
    }
  },

  /**
   * Get paginated list of pending invitations
   */
  async listPendingInvitations(
    page: number = 1,
    pageSize: number = 10
  ): Promise<PaginatedInvitations> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await apiClient.get(`invitations?${params.toString()}`);
      const data = await response.json<{
        invitations: DbInvitation[];
        total: number;
        page: number;
        pageSize: number;
        totalPages: number;
      }>();

      const invitations = data.invitations.map(transformDbInvitationToFrontend);

      return {
        invitations,
        total: data.total,
        page: data.page,
        pageSize: data.pageSize,
        totalPages: data.totalPages,
      };
    } catch (error) {
      console.error('Error fetching invitations:', error);
      if (error instanceof HTTPError) {
        const errorData = await error.response.json<{ error: string }>();
        throw new Error(errorData.error || 'Failed to fetch invitations');
      }
      throw error;
    }
  },

  /**
   * Verify an invitation token (public endpoint)
   */
  async verifyInvitation(token: string): Promise<VerifyInvitationResponse> {
    try {
      const response = await apiClient.get(`invitations/verify/${token}`);
      const data = await response.json<VerifyInvitationResponse>();

      return data;
    } catch (error) {
      console.error('Error verifying invitation:', error);
      if (error instanceof HTTPError) {
        const errorData = await error.response.json<{ error: string }>();
        return {
          valid: false,
          error: errorData.error || 'Failed to verify invitation',
        };
      }
      return {
        valid: false,
        error: 'Failed to verify invitation',
      };
    }
  },

  /**
   * Complete an invitation and create user account
   */
  async completeInvitation(
    input: CompleteInvitationInput
  ): Promise<{ message: string }> {
    try {
      const response = await apiClient.post('invitations/complete', {
        json: input,
      });

      const data = await response.json<{
        user: unknown;
        message: string;
      }>();

      return { message: data.message };
    } catch (error) {
      console.error('Error completing invitation:', error);
      if (error instanceof HTTPError) {
        const errorData = await error.response.json<{ error: string }>();
        throw new Error(errorData.error || 'Failed to complete invitation');
      }
      throw error;
    }
  },

  /**
   * Revoke (cancel) an invitation
   */
  async revokeInvitation(invitationId: string): Promise<void> {
    try {
      await apiClient.delete(`invitations/${invitationId}`);
    } catch (error) {
      console.error('Error revoking invitation:', error);
      if (error instanceof HTTPError) {
        const errorData = await error.response.json<{ error: string }>();
        throw new Error(errorData.error || 'Failed to revoke invitation');
      }
      throw error;
    }
  },

  /**
   * Resend an invitation email
   */
  async resendInvitation(email: string): Promise<UserInvitation> {
    try {
      const response = await apiClient.post('invitations/resend', {
        json: { email },
      });

      const data = await response.json<{
        invitation: DbInvitation;
        message: string;
      }>();

      return transformDbInvitationToFrontend(data.invitation);
    } catch (error) {
      console.error('Error resending invitation:', error);
      if (error instanceof HTTPError) {
        const errorData = await error.response.json<{ error: string }>();
        throw new Error(errorData.error || 'Failed to resend invitation');
      }
      throw error;
    }
  },
};
